import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef } from "react";
import { useParticipantSearch, useUsersCache } from "../../hooks";
import { useCurrentCustomerId } from "@pulse/hooks/useCustomerId";
import { ParticipantSelectorLoading, ParticipantSelectorUser } from "./ParticipantSelectorUser";
import { useTranslation } from "react-i18next";

export default function VirtualizedUsersList() {
    const { t } = useTranslation();
    const { debouncedSearch } = useParticipantSearch();
    const customerId = useCurrentCustomerId();
    const { users, fetchNextPage, hasNextPage, isFetchingNextPage } = useUsersCache(
        debouncedSearch,
        customerId,
    );
    const parentRef = useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer({
        count: users.total,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 56, // h-14 (56px), size of ParticipantSelectorUser
        overscan: 5,
    });
    const virtualItems = rowVirtualizer.getVirtualItems();

    useEffect(() => {
        const lastItem = virtualItems[virtualItems.length - 1];

        if (!lastItem) {
            return;
        }

        if (lastItem.index >= users.data.length - 1 && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [
        hasNextPage,
        fetchNextPage,
        users.data.length,
        rowVirtualizer,
        isFetchingNextPage,
        virtualItems,
    ]);
    return (
        <div
            className="relative h-[calc(100dvh-24rem)] max-h-44 w-full overflow-y-auto sm:max-h-50"
            ref={parentRef}
        >
            <div
                style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: "100%",
                    position: "relative",
                }}
            >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const user = users.data[virtualRow.index];
                    const isLoaderRow = virtualRow.index > users.data.length - 1;
                    return (
                        <div
                            key={virtualRow.index}
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: `${virtualRow.size}px`,
                                transform: `translateY(${virtualRow.start}px)`,
                            }}
                        >
                            {isLoaderRow ? (
                                hasNextPage ? (
                                    <ParticipantSelectorLoading />
                                ) : (
                                    <div>{t("userSelector.noMoreResults")}</div>
                                )
                            ) : (
                                <ParticipantSelectorUser key={virtualRow.index} user={user} />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
